import type {
  ComputedFieldDefinition,
  ComputedFieldDefinitionId,
} from "@repo/app-types/entities/computedField";
import {
  checkPlaybookHasKnowledgeBaseAdherence,
  getOutcomeDefinitionFromPlaybook,
  type PlaybookId,
  type PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type {
  GradingsByGroupSort,
  TeamPerformanceByGroupRow,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
} from "react";
import { useNavigate } from "react-router";
import { useAllComputedFieldDefinitionsQuery } from "../../api/entities/computedField.js";
import { usePlaybookTeamPerformanceByGroupPageInfiniteQuery } from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { appPaths } from "../../app/appPaths.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { buildTeamPerformancePageQueryArgs } from "../filters/buildFilterQueryArgs.js";
import { FilterContext } from "../filters/filterContext.js";
import { useFilters, type RenderFiltersOptions } from "../filters/filters.js";
import type { FilterValues } from "../filters/filterState.js";
import { usePlaybookFilterDefaults } from "../filters/usePlaybookFilterDefaults.js";
import { GroupsTable } from "./groupsTable.js";
import { DisplayColumnsSelector } from "./hooks/displayColumnsSelector.js";
import { usePerformanceMetricColumnSelector } from "./hooks/usePerformanceMetricColumnSelector.js";
import { useSectionColumnSelector } from "./hooks/useSectionColumnSelector.js";
import {
  buildPerformanceMetricColumnOptions,
  readComputedFieldIdFromColumnId,
} from "./performanceMetricColumns.js";

export function useGradingsByGroup(playbookId: PlaybookId) {
  const playbookFilterDefaults = usePlaybookFilterDefaults(playbookId, "team");
  const [filterValues, renderFilters] = useFilters(
    [
      "byGroupTeammateGroupIds",
      "dateRange",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "recordingCount",
      "dateBasis",
    ],
    { playbookId, view: "team" },
    playbookFilterDefaults,
  );

  const { data: playbook } = usePlaybookByIdQuery(playbookId);
  const {
    showsPlaybookColumn,
    selectedSections,
    selectSections: sectionColumnSections,
    selectedIds: sectionColumnSelectedIds,
    onSelectionChange: onSectionColumnSelectionChange,
  } = useSectionColumnSelector({ playbook });

  const isCustomerExperienceScoreEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.CustomerExperienceScore,
  );
  const { data: computedFields } = useAllComputedFieldDefinitionsQuery();
  const metricColumnOptions = useMemo(
    () =>
      buildPerformanceMetricColumnOptions({
        outcomeDefinition: getOutcomeDefinitionFromPlaybook(playbook),
        shouldShowKnowledgeBaseAdherenceColumn:
          playbook !== undefined &&
          checkPlaybookHasKnowledgeBaseAdherence(playbook),
        shouldShowCustomerExperienceScoreColumn:
          isCustomerExperienceScoreEnabled,
        computedFields,
      }),
    [playbook, isCustomerExperienceScoreEnabled, computedFields],
  );
  const {
    selectedMetricColumnIds,
    selectSections: metricColumnSections,
    selectedIds: metricColumnSelectedIds,
    onSelectionChange: onMetricColumnSelectionChange,
  } = usePerformanceMetricColumnSelector({
    playbook,
    availableOptions: metricColumnOptions,
  });

  const displayColumnsSelector = (
    <DisplayColumnsSelector
      metricColumnSections={metricColumnSections}
      metricColumnSelectedIds={metricColumnSelectedIds}
      onMetricColumnSelectionChange={onMetricColumnSelectionChange}
      sectionColumnSections={sectionColumnSections}
      sectionColumnSelectedIds={sectionColumnSelectedIds}
      onSectionColumnSelectionChange={onSectionColumnSelectionChange}
      customFieldSections={[]}
      customFieldSelectedIds={[]}
      onCustomFieldSelectionChange={() => {}}
    />
  );

  // We need the query hook to be inside the table component so the hook's cache state is properly reset when switching between teammate/group modes. We use an event handler to get the table's fetching state that we show next to the filters.
  const [isFetching, setIsFetching] = useState(false);

  return {
    renderFilters: (options?: Omit<RenderFiltersOptions, "isLoading">) =>
      renderFilters({
        ...options,
        isLoading: isFetching,
      }),
    displayColumnsSelector,
    table: (
      <TrainingTeamPageByGroupTable
        onFetchingChange={setIsFetching}
        playbookId={playbookId}
        showsPlaybookColumn={showsPlaybookColumn}
        selectedSections={selectedSections}
        selectedMetricColumnIds={selectedMetricColumnIds}
        computedFields={computedFields ?? []}
        filterValues={filterValues}
      />
    ),
  };
}

interface TrainingTeamPageByGroupTableProps {
  onFetchingChange: (isFetching: boolean) => void;
  playbookId: PlaybookId;
  showsPlaybookColumn: boolean;
  selectedSections: readonly PlaybookSectionNode[];
  selectedMetricColumnIds: ReadonlySet<string>;
  computedFields: readonly ComputedFieldDefinition[];
  filterValues: Pick<
    FilterValues,
    | "byGroupTeammateGroupIds"
    | "dateRange"
    | "outcome"
    | "customFields"
    | "playbookConditions"
    | "signalId"
    | "signalMetadataFields"
    | "totalRecordingDuration"
    | "recordingCount"
    | "dateBasis"
  >;
}

const TrainingTeamPageByGroupTable: FC<TrainingTeamPageByGroupTableProps> = (
  props,
) => {
  const {
    playbookId,
    showsPlaybookColumn,
    selectedSections,
    selectedMetricColumnIds,
    computedFields,
    filterValues,
  } = props;

  const selectedComputedFieldIds = useMemo<
    readonly ComputedFieldDefinitionId[]
  >(() => {
    const ids: ComputedFieldDefinitionId[] = [];
    for (const columnId of selectedMetricColumnIds) {
      const id = readComputedFieldIdFromColumnId(columnId);
      if (id !== undefined) {
        ids.push(id);
      }
    }
    return ids;
  }, [selectedMetricColumnIds]);

  const selectedComputedFields = useMemo(
    () =>
      selectedComputedFieldIds
        .map((id) => computedFields.find((cf) => cf.id === id))
        .filter((cf) => cf !== undefined),
    [selectedComputedFieldIds, computedFields],
  );

  const navigate = useNavigate();
  const filterContext = useContext(FilterContext);

  const [sort, setSort] = useState<GradingsByGroupSort | undefined>({
    field: "group_name",
    direction: "asc",
  });

  const now = useNow();
  const { data: playbook } = usePlaybookByIdQuery(playbookId);
  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

  const {
    byGroupTeammateGroupIds,
    dateRange,
    outcome,
    customFields,
    playbookConditions,
    signalId,
    signalMetadataFields,
    totalRecordingDuration,
    recordingCount,
    dateBasis,
  } = filterValues;

  const queryArg = useMemo(() => {
    const { scope, filters } = buildTeamPerformancePageQueryArgs(now, {
      playbookId,
      dateRange,
      teammateGroupIds: byGroupTeammateGroupIds,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      recordingCount,
      dateBasis,
    });
    return {
      playbookId,
      scope,
      filters,
      playbookSectionIds: selectedSections.map((section) => section.id),
      computedFieldIds: [...selectedComputedFieldIds],
      sort,
    };
  }, [
    now,
    byGroupTeammateGroupIds,
    playbookId,
    dateRange,
    outcome,
    customFields,
    playbookConditions,
    signalId,
    signalMetadataFields,
    totalRecordingDuration,
    recordingCount,
    dateBasis,
    selectedSections,
    selectedComputedFieldIds,
    sort,
  ]);

  const { data, isLoading, isFetching, isError, hasNextPage, fetchNextPage } =
    usePlaybookTeamPerformanceByGroupPageInfiniteQuery(queryArg);

  const onFetchingChange = useEffectEvent(props.onFetchingChange ?? (() => {}));
  useEffect(() => {
    onFetchingChange(isFetching);
  }, [isFetching, onFetchingChange]);

  const rows = useMemo(
    () => data?.pages.flatMap((page) => page.rows) ?? [],
    [data],
  );

  const onFiltersChange = filterContext.onChange;
  const onGroupAction = useCallback(
    (row: TeamPerformanceByGroupRow) => {
      const groupId = row.id;

      onFiltersChange({ teammateGroupIds: [groupId] });
      navigate(appPaths.playbookTeammates.build({ playbookId }));
    },
    [playbookId, onFiltersChange, navigate],
  );

  // Build lookup map for median section scores (section_id → median score)
  const benchmarkSectionScores = data?.pages.at(0)?.benchmarkSectionScores;
  const medianSectionScores = useMemo(() => {
    if (!benchmarkSectionScores) {
      return undefined;
    }
    const result: Record<string, number> = {};
    for (const item of benchmarkSectionScores) {
      result[item.criteria_node_id] = item.median_average_score;
    }
    return result;
  }, [benchmarkSectionScores]);

  const {
    median_pass_rate: medianPassRate,
    median_talk_ratio: medianTalkRatio,
    median_outcome_green_rate: medianOutcomeGreenRate,
    median_outcome_green_count: medianOutcomeGreenCount,
    average_revenue_benchmark: averageRevenueBenchmark,
    median_revenue_per_hour: medianRevenuePerHour,
    median_seconds_per_green_outcome: medianSecondsPerGreenOutcome,
    median_customer_experience_score: medianCustomerExperienceScore,
    computed_field_medians: medianComputedFieldValues,
  } = data?.pages.at(0)?.benchmarkScores ?? {};

  const isCustomerExperienceScoreEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.CustomerExperienceScore,
  );

  return (
    <GroupsTable
      rows={rows}
      medianPassRate={medianPassRate}
      medianTalkRatio={medianTalkRatio}
      medianOutcomeGreenRate={medianOutcomeGreenRate}
      medianOutcomeGreenCount={medianOutcomeGreenCount}
      averageRevenueBenchmark={averageRevenueBenchmark}
      medianRevenuePerHour={medianRevenuePerHour}
      medianSecondsPerGreenOutcome={medianSecondsPerGreenOutcome}
      medianCustomerExperienceScore={medianCustomerExperienceScore}
      outcomeDefinition={outcomeDefinition}
      shouldShowKnowledgeBaseAdherenceColumn={
        playbook !== undefined &&
        checkPlaybookHasKnowledgeBaseAdherence(playbook)
      }
      shouldShowCustomerExperienceScoreColumn={isCustomerExperienceScoreEnabled}
      showsPlaybookColumn={showsPlaybookColumn}
      selectedSections={selectedSections}
      medianSectionScores={medianSectionScores}
      computedFields={selectedComputedFields}
      medianComputedFieldValues={medianComputedFieldValues ?? undefined}
      selectedMetricColumnIds={selectedMetricColumnIds}
      sort={sort}
      onSortChange={setSort}
      isLoading={isLoading}
      isError={isError}
      onLoadMore={hasNextPage ? fetchNextPage : undefined}
      onGroupAction={onGroupAction}
      isFullBleed
    />
  );
};
